'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { PanelLeftCloseIcon, PanelLeftOpenIcon, XIcon } from 'lucide-react';
import { Slot } from 'radix-ui';
import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type FC,
  type ReactElement,
  type ReactNode,
} from 'react';

import { ForceDesktopSidebar } from '../../hooks/use-mobile';
import { cn } from '../../lib/utils';
import { Badge } from '../Badge';
import { SolinkLogo } from '../SolinkLogo';
import { TooltipContent as AppTooltipContent } from '../Tooltip';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from '../ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

/**
 * Solink application navigation sidebar.
 *
 * Built on the shadcn `Sidebar` block, which supplies the open/collapsed state,
 * the ⌘B shortcut, and the layout primitives. This layer owns persistence,
 * Solink spacing/typography, and keeps the collapsible rail at every viewport
 * width (phones never reach this surface — VD routes them to MobileLanding).
 *
 * The items are written from scratch rather than wrapping `SidebarMenuButton`,
 * whose collapsed sizing uses `!important` and would need to be fought on every
 * property.
 *
 * This component is deliberately free of routing and permission concepts. Pass
 * a router link through `asChild` and decide item visibility in the consuming
 * app.
 *
 * Layout contract for pages:
 * - In-flow content inside `#vd-app` should size from its parent (`100%` / flex /
 *   container measurement) — never `100vw` or `window.innerWidth`.
 * - `position: fixed` chrome that clears the rail should offset with
 *   `var(--desktopNavWidth)`, which this provider keeps in sync (240 ↔ 60) and
 *   restores to the legacy 80px on unmount.
 */

/** Expanded width, per the Figma navigation-sidebar component. */
export const NAV_SIDEBAR_WIDTH = '240px';
/** Collapsed (icon-only) width. */
export const NAV_SIDEBAR_WIDTH_ICON = '60px';

/**
 * Legacy VD desktop nav width. Restored on unmount so `--desktopNavWidth`
 * consumers fall back correctly when the redesign shell is off.
 */
const LEGACY_DESKTOP_NAV_WIDTH = '80px';

/**
 * Rail width + padding timing. Fast-out, soft-settle (MUI mini-variant is
 * ~195–225ms sharp; we use a slightly springier curve for the 180px travel).
 */
const NAV_WIDTH_MS = 200;
/** Shared duration for padding / item size transitions (easing lives in globals.css). */
const NAV_WIDTH_TRANSITION = 'duration-200 ease-out motion-reduce:transition-none';
const NAV_PADDING_TRANSITION = `transition-[padding] ${NAV_WIDTH_TRANSITION}`;

/**
 * Expanded-only chrome (labels, promo, profile text). Settled collapsed = hidden
 * via opacity; direction-aware leave/arrive delays live in globals.css keyed off
 * `data-nav-transition` so content leaves fast and arrives after the rail opens.
 */
const NAV_FADE_EXPANDED =
  'transition-opacity motion-reduce:transition-none group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0';

/**
 * App-nav persistence key. Distinct from shadcn's `sidebar_state` cookie so the
 * SDM day-parts sidebar cannot change what the app nav restores on mount.
 */
const NAV_SIDEBAR_OPEN_STORAGE_KEY = 'solink-nav-sidebar-open';

type NavTransition = 'collapsing' | 'expanding';

/**
 * Tracks the in-flight width gesture so CSS can pin the inner panel at 240px
 * (no text reflow) and orchestrate leave/arrive fades. Cleared on `transitionend`
 * for `width`, with a timeout fallback if the event is skipped.
 */
function useNavTransition(): NavTransition | null {
  const { state } = useSidebar();
  const [transition, setTransition] = useState<NavTransition | null>(null);
  const prevStateRef = useRef(state);

  useLayoutEffect(() => {
    if (prevStateRef.current === state) return;
    const next: NavTransition = state === 'collapsed' ? 'collapsing' : 'expanding';
    prevStateRef.current = state;
    setTransition(next);

    const container = document.querySelector<HTMLElement>('[data-slot="sidebar-container"]');

    const clear = () => setTransition(null);
    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'width') return;
      clear();
    };

    container?.addEventListener('transitionend', onEnd);
    const timeout = window.setTimeout(clear, NAV_WIDTH_MS + 50);

    return () => {
      container?.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timeout);
    };
  }, [state]);

  // Reduced-motion: skip the in-flight attribute so settled styles apply immediately.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setTransition(null);
  }, [state]);

  return transition;
}

function readNavSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(NAV_SIDEBAR_OPEN_STORAGE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // private mode / quota — fall through
  }
  return true;
}

function writeNavSidebarOpen(open: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NAV_SIDEBAR_OPEN_STORAGE_KEY, String(open));
  } catch {
    // ignore
  }
}

/**
 * Keeps the app-wide `--desktopNavWidth` CSS variable in sync with the rail
 * (240 ↔ 60) and marks `<html data-aspen-nav>` so SASS can keep develop defaults
 * when the redesign shell is off. Pages that still use `position: fixed` + nav
 * offsets read the var (or the data attribute) instead of a hardcoded 80px.
 */
const NavSidebarWidthSync: FC = () => {
  const { state } = useSidebar();
  const width = state === 'collapsed' ? NAV_SIDEBAR_WIDTH_ICON : NAV_SIDEBAR_WIDTH;

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--desktopNavWidth', width);
    root.dataset.aspenNav = 'true';
    return () => {
      root.style.setProperty('--desktopNavWidth', LEGACY_DESKTOP_NAV_WIDTH);
      delete root.dataset.aspenNav;
    };
  }, [width]);

  return null;
};

// ---------------------------------------------------------------------------
// Provider & root
// ---------------------------------------------------------------------------

export const NavSidebarProvider: FC<ComponentProps<typeof SidebarProvider>> = ({
  className,
  style,
  defaultOpen,
  open: openProp,
  onOpenChange,
  children,
  ...props
}) => {
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    () => defaultOpen ?? readNavSidebarOpen(),
  );
  const open = isControlled ? openProp : uncontrolledOpen;

  // App-nav open state lives in `solink-nav-sidebar-open` (localStorage).
  // Underlying shadcn SidebarProvider still writes a `sidebar_state` cookie —
  // acceptable until someone regularly toggles both app nav and SDM day-parts.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
        writeNavSidebarOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  return (
    <ForceDesktopSidebar>
      <SidebarProvider
        open={open}
        onOpenChange={handleOpenChange}
        className={cn(
          'tw min-h-0',
          // Keep the rail visible below the md breakpoint (phones never hit this surface).
          '[&_[data-slot=sidebar]]:!block',
          '[&_[data-slot=sidebar-container]]:!flex',
          // One timing for the width animation (overrides shadcn duration-200 ease-linear).
          // Gap/container widths + easing come from the Tailwind v3 shim in
          // nav-sidebar.css (pulled in via globals.css).
          '[&_[data-slot=sidebar-gap]]:duration-200',
          '[&_[data-slot=sidebar-gap]]:motion-reduce:transition-none',
          '[&_[data-slot=sidebar-container]]:duration-200',
          '[&_[data-slot=sidebar-container]]:motion-reduce:transition-none',
          className,
        )}
        style={
          {
            '--sidebar-width': NAV_SIDEBAR_WIDTH,
            '--sidebar-width-icon': NAV_SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        {...props}
      >
        <NavSidebarWidthSync />
        {children}
      </SidebarProvider>
    </ForceDesktopSidebar>
  );
};

// English defaults (aria-label, toggle, dismiss) stay until SL-16309 adds
// optional label props; callers will pass t() then. Do not import locale here.
export const NavSidebar: FC<ComponentProps<typeof Sidebar>> = ({ className, ...props }) => {
  const transition = useNavTransition();

  return (
    <Sidebar
      {...props}
      collapsible="icon"
      role="navigation"
      aria-label="Main"
      data-nav-transition={transition ?? undefined}
      className={cn(
        'tw select-none',
        // Hairline is the only thing dividing rail from page: under .dark the rail
        // (background-base) and the page background both resolve to Gray-950, so
        // dropping it here leaves no edge. Same token as DesktopHeader's border-bottom.
        'group-data-[side=left]:border-border-muted',
        className,
      )}
    />
  );
};

// ---------------------------------------------------------------------------
// Regions
// ---------------------------------------------------------------------------

/** Branding row at the top of the sidebar. */
export const NavSidebarHeader: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <SidebarHeader
    data-slot="nav-sidebar-header"
    className={cn(
      'sticky top-0 z-10 flex-row items-center gap-2 bg-sidebar px-6 py-4',
      NAV_PADDING_TRANSITION,
      // 60px rail cannot fit px-6 (24+24) beside a 28px mark — center with no side pad.
      'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0',
      className,
    )}
    {...props}
  />
);

const navSidebarBrandLogos = (
  <>
    <SolinkLogo
      alt="Solink"
      data-nav-fade="expanded"
      className="col-start-1 row-start-1 transition-opacity group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0 motion-reduce:transition-none"
    />
    <SolinkLogo
      variant="mark"
      alt=""
      aria-hidden
      data-nav-fade="collapsed"
      className="col-start-1 row-start-1 opacity-0 transition-opacity group-data-[collapsible=icon]:opacity-100 motion-reduce:transition-none"
    />
  </>
);

export type NavSidebarBrandProps = ComponentPropsWithoutRef<'a'> & {
  /** Merge onto a router `Link` (or other single child) instead of rendering `<a>`. */
  asChild?: boolean;
};

/**
 * Default Solink wordmark ↔ mark cross-fade for the header. Owns the collapse
 * opacity classes so consumers (app + Storybook) do not restyle the logos.
 */
export const NavSidebarBrand = forwardRef<HTMLAnchorElement, NavSidebarBrandProps>(
  (
    {
      asChild = false,
      className,
      children,
      href = '#',
      'aria-label': ariaLabel = 'Solink home',
      ...props
    },
    ref,
  ) => {
    const brandClass = cn('grid h-7 shrink-0 place-items-center', className);

    if (asChild) {
      const child = Children.only(children);
      if (!isValidElement(child)) {
        throw new Error('NavSidebarBrand asChild expects a single React element child');
      }
      return cloneElement(child as ReactElement<Record<string, unknown>>, {
        ...props,
        ref,
        'data-slot': 'nav-sidebar-brand',
        className: cn(brandClass, (child.props as { className?: string }).className),
        'aria-label': (child.props as { 'aria-label'?: string })['aria-label'] ?? ariaLabel,
        children: navSidebarBrandLogos,
      });
    }

    return (
      <a
        ref={ref}
        data-slot="nav-sidebar-brand"
        href={href}
        aria-label={ariaLabel}
        className={brandClass}
        {...props}
      >
        {navSidebarBrandLogos}
      </a>
    );
  },
);
NavSidebarBrand.displayName = 'NavSidebarBrand';

/** Scrollable region holding the nav groups. */
export const NavSidebarBody: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <SidebarContent
    data-slot="nav-sidebar-body"
    className={cn(
      // Figma expanded + collapsed both use 16px gaps between groups (alias/space/md).
      // Classic scrollbar (~6px) sits on the inline-end edge and is not part of
      // padding, so equal px-* reads heavier on the right. Use pl-3 / pr-1.5
      // (12px / 6px) so content→rail and content→thumb both land near 12px.
      'gap-4 pl-3 pr-1.5',
      NAV_PADDING_TRANSITION,
      'group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-2',
      // Collapsed: still scrollable, but no visible bar (shadcn defaults to overflow-hidden).
      'group-data-[collapsible=icon]:!overflow-y-auto',
      'group-data-[collapsible=icon]:[scrollbar-width:none]',
      'group-data-[collapsible=icon]:[&::-webkit-scrollbar]:hidden',
      className,
    )}
    {...props}
  />
);

/**
 * A generic full-width slot below the scroll region — used for the locations
 * filter and anything else that sits above the promo/actions.
 */
export const NavSidebarSection: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <div
    data-slot="nav-sidebar-section"
    data-nav-fade="expanded"
    className={cn(
      // min-w-0 so children (e.g. LocationSelector) can shrink below their CSS min-width.
      // pt-2 (8px) separates the scroll nav from the locations control.
      // pl/pr match NavSidebarBody (scrollbar compensation on the scrollport only —
      // this section has no bar, so keep symmetric px-3).
      'flex w-full min-w-0 shrink-0 flex-col px-3 pb-4 pt-2',
      NAV_FADE_EXPANDED,
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
);

export const NavSidebarFooter: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <SidebarFooter
    data-slot="nav-sidebar-footer"
    className={cn(
      'gap-2 px-3 pb-3 pt-3',
      NAV_PADDING_TRANSITION,
      'group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0',
      className,
    )}
    {...props}
  />
);

// ---------------------------------------------------------------------------
// Visibility helpers
// ---------------------------------------------------------------------------

/** Renders children only while the sidebar is expanded. */
export const NavSidebarExpandedOnly: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <div
    data-nav-fade="expanded"
    data-slot="nav-sidebar-expanded-only"
    className={cn('contents group-data-[collapsible=icon]:hidden', className)}
    {...props}
  />
);

/**
 * Renders children only while the sidebar is collapsed. Uses a real box (not
 * `contents`) so opacity fades during expand work — `display: contents` cannot
 * animate opacity on its children.
 */
export const NavSidebarCollapsedOnly: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <div
    data-nav-fade="collapsed"
    data-slot="nav-sidebar-collapsed-only"
    className={cn(
      'hidden w-full justify-center transition-opacity motion-reduce:transition-none',
      'group-data-[collapsible=icon]:flex',
      className,
    )}
    {...props}
  />
);

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export interface NavSidebarGroupProps extends ComponentProps<'div'> {
  /**
   * Section heading. Omit for the first, unlabelled group. When the sidebar is
   * collapsed the heading is hidden; group separation comes from the body gap.
   */
  label?: string;
}

export const NavSidebarGroup: FC<NavSidebarGroupProps> = ({
  label,
  className,
  children,
  ...props
}) => (
  <div
    data-slot="nav-sidebar-group"
    className={cn('flex w-full flex-col', 'group-data-[collapsible=icon]:items-center', className)}
    {...props}
  >
    {label && (
      <div
        data-nav-fade="expanded"
        className={cn(
          'overflow-hidden whitespace-nowrap px-3 pb-2 uppercase text-foreground-secondary label-sm',
          NAV_FADE_EXPANDED,
          'group-data-[collapsible=icon]:h-0 group-data-[collapsible=icon]:overflow-hidden group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:pb-0',
        )}
      >
        {label}
      </div>
    )}
    <SidebarMenu className="gap-0 group-data-[collapsible=icon]:items-center">
      {children}
    </SidebarMenu>
  </div>
);

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

const navSidebarItemVariants = cva(
  [
    'relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-left',
    'outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring',
    // Figma nav glyphs are #767B7B (foreground-secondary); labels stay primary.
    '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground-secondary [&_svg]:stroke-current',
    // Collapsed: 32×32 tile, icon centered — drop gap so the hidden label cannot shove it.
    'group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:items-center',
    'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0',
    'group-data-[collapsible=icon]:p-2',
    // Soften the padding snap between expanded and icon tiles.
    `transition-[padding,width,height,colors] ${NAV_WIDTH_TRANSITION}`,
  ],
  {
    variants: {
      isActive: {
        // Mirror Aspen mobile DrawerNavItem: gray elevated pill + teal icon.
        // Hover uses elevated/60 so it stays below active and visible in dark mode
        // (background-muted equals background-base under .dark → silent no-op).
        true: 'bg-background-elevated [&_svg]:text-foreground-accent',
        false: 'hover:bg-background-elevated/60',
      },
      disabled: {
        true: 'pointer-events-none text-foreground-muted [&_svg]:text-foreground-muted',
        false: 'text-foreground-primary',
      },
    },
    defaultVariants: { isActive: false, disabled: false },
  },
);

export interface NavSidebarItemProps
  extends Omit<ComponentProps<'button'>, 'disabled'>, VariantProps<typeof navSidebarItemVariants> {
  /** Render as the child element — pass a router link here. */
  asChild?: boolean;
  /**
   * Label shown in a tooltip while collapsed. Without it a collapsed item has
   * no accessible name beyond its icon.
   */
  tooltip?: string;
}

export const NavSidebarItem: FC<NavSidebarItemProps> = ({
  asChild = false,
  isActive = false,
  disabled = false,
  tooltip,
  className,
  ...props
}) => {
  const { state, isMobile } = useSidebar();
  const Comp = asChild ? Slot.Root : 'button';

  const item = (
    <Comp
      data-slot="nav-sidebar-item"
      data-active={isActive}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      className={cn(navSidebarItemVariants({ isActive, disabled }), className)}
      {...props}
    />
  );

  return (
    <SidebarMenuItem className="group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{item}</TooltipTrigger>
          <TooltipContent side="right" align="center" hidden={state !== 'collapsed' || isMobile}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        item
      )}
    </SidebarMenuItem>
  );
};

/** Item text. Fades out with the rail width so collapse reads as one gesture. */
export const NavSidebarItemLabel: FC<ComponentProps<'span'>> = ({ className, ...props }) => (
  <span
    data-slot="nav-sidebar-item-label"
    data-nav-fade="expanded"
    className={cn(
      'min-w-0 flex-1 truncate label-md',
      NAV_FADE_EXPANDED,
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
);

/** Optional secondary line beneath the item label. */
export const NavSidebarItemSublabel: FC<ComponentProps<'span'>> = ({ className, ...props }) => (
  <span
    data-slot="nav-sidebar-item-sublabel"
    data-nav-fade="expanded"
    className={cn(
      'w-full truncate text-foreground-secondary caption',
      NAV_FADE_EXPANDED,
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
);

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

const navSidebarBadgeCollapsed = {
  // Text badges are expanded-only; critical becomes the 8px rail dot.
  hideWhenCollapsed: 'group-data-[collapsible=icon]:hidden',
  criticalDot: [
    'group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:left-[18px]',
    'group-data-[collapsible=icon]:top-[10px] group-data-[collapsible=icon]:size-2',
    'group-data-[collapsible=icon]:h-2 group-data-[collapsible=icon]:min-h-0',
    'group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:border-0',
    'group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:[&>*]:hidden',
  ].join(' '),
} as const;

const navSidebarBadgeTone = {
  neutral: {
    variant: 'outline' as const,
    className: cn(
      'border-border-muted text-interaction-neutral-content-default',
      navSidebarBadgeCollapsed.hideWhenCollapsed,
    ),
  },
  accent: {
    variant: 'primary' as const,
    className: navSidebarBadgeCollapsed.hideWhenCollapsed,
  },
  critical: {
    variant: 'destructive' as const,
    className: navSidebarBadgeCollapsed.criticalDot,
  },
};

export interface NavSidebarBadgeProps extends Omit<
  ComponentProps<typeof Badge>,
  'variant' | 'tone'
> {
  tone?: keyof typeof navSidebarBadgeTone;
}

/** Nav-sized solink-ui Badge with collapsed-rail behaviour. */
export const NavSidebarBadge: FC<NavSidebarBadgeProps> = ({
  tone = 'neutral',
  className,
  children,
  ...props
}) => {
  const mapped = navSidebarBadgeTone[tone];
  return (
    <Badge
      data-slot="nav-sidebar-badge"
      variant={mapped.variant}
      className={cn('h-5 min-h-5 px-2 py-1', mapped.className, className)}
      {...props}
    >
      {children}
    </Badge>
  );
};

// ---------------------------------------------------------------------------
// Promo card
// ---------------------------------------------------------------------------

/**
 * Accent callout card, e.g. the "Solink Agents" upsell.
 * Collapse is instant (no transition). Expand animates the slot open, then
 * fades the interior in after a short delay — see globals.css.
 */
export const NavSidebarPromo: FC<ComponentProps<'div'>> = ({ className, children, ...props }) => (
  <div
    data-slot="nav-sidebar-promo-slot"
    className={cn(
      'grid grid-rows-[1fr] motion-reduce:transition-none',
      'group-data-[collapsible=icon]:grid-rows-[0fr]',
    )}
  >
    <div className="min-h-0 overflow-hidden">
      <div
        data-slot="nav-sidebar-promo"
        className={cn(
          'mx-3 mb-4 flex shrink-0 flex-col rounded-sm border border-border-accent bg-background-accent p-3',
          className,
        )}
        {...props}
      >
        <div
          data-slot="nav-sidebar-promo-interior"
          className={cn(
            // Card owns padding; gap stacks title / description; action uses mt-auto.
            'flex flex-1 flex-col gap-2',
            // opacity-0 alone stays focusable; invisible drops Dismiss/CTA from the tab order.
            'group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:invisible group-data-[collapsible=icon]:opacity-0',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  </div>
);

export const NavSidebarPromoTitle: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <div
    data-slot="nav-sidebar-promo-title"
    className={cn('flex items-center gap-2 text-foreground-primary heading-md', className)}
    {...props}
  />
);

export const NavSidebarPromoDescription: FC<ComponentProps<'p'>> = ({ className, ...props }) => (
  <p
    data-slot="nav-sidebar-promo-description"
    className={cn('text-foreground-secondary label-md', className)}
    {...props}
  />
);

/** Text CTA under the promo description (e.g. “Set up an agent →”). */
export const NavSidebarPromoAction: FC<ComponentProps<'button'> & { asChild?: boolean }> = ({
  asChild = false,
  className,
  ...props
}) => {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      data-slot="nav-sidebar-promo-action"
      className={cn(
        'mt-auto flex items-center gap-2 text-left text-foreground-primary label-md',
        'outline-hidden hover:text-foreground-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
};

/** Dismiss control for the promo title row (right-aligned X). */
export const NavSidebarPromoDismiss: FC<ComponentProps<'button'>> = ({
  className,
  'aria-label': ariaLabel = 'Dismiss',
  ...props
}) => (
  <button
    type="button"
    data-slot="nav-sidebar-promo-dismiss"
    aria-label={ariaLabel}
    className={cn(
      '-mr-1 ml-auto flex size-6 shrink-0 items-center justify-center rounded-sm',
      'outline-hidden text-foreground-secondary transition-colors',
      'hover:bg-background-elevated/60 hover:text-foreground-primary',
      'focus-visible:ring-2 focus-visible:ring-sidebar-ring',
      '[&_svg]:size-4 [&_svg]:shrink-0',
      className,
    )}
    {...props}
  >
    <XIcon />
  </button>
);

// ---------------------------------------------------------------------------
// Utility actions
// ---------------------------------------------------------------------------

/**
 * Actions cluster + collapse toggle above the profile footer. Owns the
 * expanded row / collapsed column layout so AppSidebar and Storybook share one
 * default instead of duplicating wrapper classes.
 */
export const NavSidebarToolbar: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <div
    data-slot="nav-sidebar-toolbar"
    className={cn(
      'flex w-full flex-row items-center px-3',
      'group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center',
      'group-data-[collapsible=icon]:gap-4 group-data-[collapsible=icon]:px-0',
      // Actions fill the row when expanded; sit tight above the toggle when collapsed.
      '[&_[data-slot=nav-sidebar-actions]]:min-w-0 [&_[data-slot=nav-sidebar-actions]]:flex-1',
      '[&_[data-slot=nav-sidebar-actions]]:px-0',
      'group-data-[collapsible=icon]:[&_[data-slot=nav-sidebar-actions]]:flex-none',
      className,
    )}
    {...props}
  />
);

/**
 * Row of icon buttons above the profile footer. Stacks vertically when the
 * sidebar is collapsed. Put the collapse toggle *outside* this row (sibling
 * inside `NavSidebarToolbar`) so Figma's 16px gap between the action cluster
 * and the toggle is preserved.
 */
export const NavSidebarActions: FC<ComponentProps<'div'>> = ({ className, ...props }) => (
  <div
    data-slot="nav-sidebar-actions"
    className={cn(
      'flex shrink-0 items-center gap-1 px-3',
      // Collapsed rail is 60px — drop side padding and center the icon column.
      'group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center',
      'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-1',
      'group-data-[collapsible=icon]:px-0',
      className,
    )}
    {...props}
  />
);

export const NavSidebarActionButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<'button'> & {
    asChild?: boolean;
    /**
     * Hover label. Defaults to `aria-label`. Pass `false` to skip the tooltip
     * wrapper (needed when this button is a Radix `DropdownMenuTrigger` `asChild`
     * target — a Tooltip root would break anchoring).
     */
    tooltip?: string | false;
    isActive?: boolean;
  }
>(
  (
    {
      asChild = false,
      className,
      tooltip,
      isActive = false,
      'aria-label': ariaLabel,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const { state, isMobile } = useSidebar();
    const Comp = asChild ? Slot.Root : 'button';
    const tip =
      tooltip === false
        ? undefined
        : (tooltip ?? (typeof ariaLabel === 'string' ? ariaLabel : undefined));

    const button = (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        data-slot="nav-sidebar-action"
        data-active={isActive || undefined}
        aria-label={ariaLabel}
        aria-pressed={isActive || undefined}
        className={cn(
          'relative flex items-center justify-center rounded-sm p-1 text-foreground-secondary transition-colors',
          'outline-hidden hover:bg-background-elevated/60 focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-current',
          isActive &&
            'bg-background-elevated text-foreground-accent [&_svg]:text-foreground-accent',
          className,
        )}
        {...props}
      />
    );

    if (!tip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <AppTooltipContent
          variant="inverse"
          side={state === 'collapsed' ? 'right' : 'top'}
          align="center"
          hidden={isMobile}
        >
          {tip}
        </AppTooltipContent>
      </Tooltip>
    );
  },
);
NavSidebarActionButton.displayName = 'NavSidebarActionButton';

/** Collapse/expand control. Sits at the end of the actions row. */
export const NavSidebarToggle: FC<Omit<ComponentProps<'button'>, 'onClick'>> = ({
  className,
  ...props
}) => {
  const { toggleSidebar, state } = useSidebar();
  const collapsed = state === 'collapsed';

  return (
    <NavSidebarActionButton
      onClick={toggleSidebar}
      aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      aria-expanded={!collapsed}
      data-testid="desk-nav-collapse-toggle"
      className={cn('ml-auto group-data-[collapsible=icon]:ml-0', className)}
      {...props}
    >
      {collapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
    </NavSidebarActionButton>
  );
};

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export interface NavSidebarProfileProps extends Omit<ComponentProps<'button'>, 'name' | 'title'> {
  asChild?: boolean;
  /** Initials shown in the avatar circle. */
  initials: string;
  name: ReactNode;
  /** Secondary line, typically the user's role. */
  description?: ReactNode;
}

// forwardRef so Radix DropdownMenuTrigger `asChild` can attach the open handler
// (React 18 does not pass refs through function components).
export const NavSidebarProfile = forwardRef<HTMLButtonElement, NavSidebarProfileProps>(
  ({ asChild = false, initials, name, description, className, type = 'button', ...props }, ref) => {
    const Comp = asChild ? Slot.Root : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        data-slot="nav-sidebar-profile"
        className={cn(
          // rounded-full matches the 40px avatar so hover highlight hugs the circle.
          'group/profile flex w-full cursor-pointer items-center gap-2 rounded-full text-left transition-colors hover:bg-background-elevated/60',
          'outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          // Collapsed: only the 40px avatar — hide the label so gap does not shift it.
          'group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:justify-center',
          'group-data-[collapsible=icon]:gap-0',
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-full label-sm',
            'bg-background-accent text-foreground-accent transition-colors',
            // One shade stronger on hover (circle + letters); no scale animation.
            'group-hover/profile:bg-primary-100 group-hover/profile:text-primary-700',
            'dark:group-hover/profile:bg-primary-800',
          )}
        >
          {initials}
        </span>
        <span
          data-nav-fade="expanded"
          className={cn(
            'flex min-w-0 flex-1 flex-col justify-center overflow-hidden',
            NAV_FADE_EXPANDED,
            'group-data-[collapsible=icon]:hidden',
          )}
        >
          <span className="truncate text-foreground-primary label-md">{name}</span>
          {description && (
            <span className="truncate text-foreground-secondary label-sm">{description}</span>
          )}
        </span>
      </Comp>
    );
  },
);
NavSidebarProfile.displayName = 'NavSidebarProfile';
